import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, PropType, ref } from 'vue';
import { ChecklistField } from '@/interfaces/components/configurator/ChecklistField';
import ChecklistBoolFieldModule from '@/components/views/productView/ChecklistBoolFieldModule.vue';
import ChecklistDropdownFieldModule from '@/components/views/productView/ChecklistDropdownFieldModule.vue';
import ChecklistFloatFieldModule from '@/components/views/productView/ChecklistFloatFieldModule.vue';
import ChecklistStringFieldModule from '@/components/views/productView/ChecklistStringFieldModule.vue';
import ChecklistIntegerFieldModule from '@/components/views/productView/ChecklistIntegerFieldModule.vue';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { determineFieldVisibility } from '@/helpers/FieldFunctionHelper';
import { ConfiguratorValue } from '@/interfaces/components/configurator/ConfiguratorValue';
import { ActiveProductFormValueObject } from '@/interfaces/general/ActiveProductFormValueObject';
import { ProductDetails } from '@/interfaces/components/configurator/ProductDetails';
// @ts-ignore
import is from 'is_js';
import RichTextEditor from '@/components/views/CmsIndex/RichTextEditor.vue';
import { addititonalDisabledChecklistFields } from '@/regex/checklistFieldsAdditionalConfiguration';
import store from '@/store';
import { ChecklistFieldEntry } from '@/interfaces/components/configurator/ChecklistFieldEntry';


export default /*#__PURE__*/_defineComponent({
  __name: 'ChecklistFieldModule',
  props: {
    checklistField: { type: Object as PropType<ChecklistField>, required: true },
    activeProductFormId: {
        type: Number as PropType<number>,
        default: null,
    },
    isAlternativeStyle: { type: Boolean, default: false },
    isFirst: { type: Boolean, default: false },
    itemRowIndex: { type: Number, default: 999 },
    isLast: { type: Boolean, default: false },
    level: { type: Number, default: 0 },
    isUpdating: { type: Boolean, default: false },
    isVisibleInMultiposition: { type: Boolean, default: false },
    isDisabled: { type: Boolean, default: false },
    pIdFieldErrors: { type: Array as PropType<string[]>, default: [] },
    addNewRow: {
        type: Function as PropType<
            (numberOfRows: number, existingProductValues?: ChecklistFieldEntry[]) => void | Promise<void>
        >,
        default: null,
    },
    debounceToggleIsActive: {
        type: Function as PropType<(state: boolean) => void>,
        default: null,
    },
    isConfigurator: { type: Boolean, default: false },
    temporaryDisabledPIDs: {
        type: Array as PropType<string[]>,
        default: () => [],
    },
    productDetails: {
        type: Object as PropType<ProductDetails | null>,
        default: null,
    },
    checklistSegmentName: { type: String },
},
  setup(__props) {

const props = __props;



const { proxy } = getCurrentInstance()!;
const imageWrapper = ref<HTMLElement | null>(null);
const richTextEditor = ref<any>(null);

const fieldType = {
    float: ChecklistFloatFieldModule,
    string: ChecklistStringFieldModule,
    boolean: ChecklistBoolFieldModule,
    integer: ChecklistIntegerFieldModule,
    dropdown: ChecklistDropdownFieldModule,
};

const isFieldVisible = ref(true);
const isModalVisible = ref(false);
const selectedChecklist = ref<ChecklistField | null>(null);
const showFullScreenImage = ref(false);
const hasError = ref(false);

const isFieldDisabled = computed(() => {
    if (props.isDisabled) {
        return true;
    }

    const field = addititonalDisabledChecklistFields.find((field) => field.pID === props.checklistField.pId);

    if (!field) {
        return false;
    }

    if (!field?.condition) {
        return true;
    }

    if (field.condition?.isRoltekUG === true) {
        return !field.condition?.UGs.includes(String(store.getters['jwtData/currentUserGroup']));
    }

    return false;
});

const determineIsFieldVisible = computed(() => {
    if (props.isAlternativeStyle) {
        return props.isVisibleInMultiposition;
    }

    return isFieldVisible.value;
});

const isRoltekUG = computed(() => store.getters['jwtData/isRoltekUserGroup']);

const labelFontClass = computed(() => (props.isAlternativeStyle ? 'u-b4' : 'u-b2-'));

const labelClasses = computed(() => {
    let classes = '';
    if (descriptionExists()) {
        classes = 'is-clickable';
    }

    classes += ` ${labelFontClass.value}`;

    return classes;
});

const className = computed(() => {
    const classes = ['c-checklist__row', `c-checklist__row--${props.checklistField.fieldType}`];
    if (props.isAlternativeStyle) {
        classes.push('is-alternative');
    }
    if (props.isFirst) {
        classes.push('is-first');
    }

    if (props.isUpdating) {
        classes.push('is-multiposition');
    }

    if (props.isUpdating && props.isVisibleInMultiposition) {
        classes.push('is-multiposition--visible');
    }

    return classes.join(' ');
});

const activeFieldModule = computed(() => fieldType[props.checklistField.fieldType as keyof typeof fieldType]);

function toggleImageFullScreen() {
    showFullScreenImage.value = !showFullScreenImage.value;
}

function checkVisibilityLevel(fieldLevel: number) {
    return fieldLevel <= props.level;
}

function descriptionExists() {
    return props.checklistField.description !== null;
}

function toggleInfoDetailsModal() {
    if (descriptionExists()) {
        isModalVisible.value = !isModalVisible.value;
        showFullScreenImage.value = false;
        if (isModalVisible.value && is.safari()) {
            nextTick(() => {
                const imageWrapperElement = imageWrapper.value as HTMLElement;
                const text = richTextEditor.value.$el as HTMLElement;
                const modalInner = document.querySelector('.ant-modal-body') as HTMLElement;
                const textHeight = text.getBoundingClientRect().height;
                const modalInnerHeight = modalInner.getBoundingClientRect().height;

                imageWrapperElement.style.height = modalInnerHeight - textHeight - 110 + 'px';
            });
        }
    }
}

async function triggerVisibilityFunctions({
    activeProductFormValues,
}: {
    activeProductFormValues: ActiveProductFormValueObject;
}) {
    // console.log('yo visibility');
    proxy!.$notification.destroy();
    if (!checklistFieldHasVisibilityFunction()) {
        return;
    }
    isFieldVisible.value = determineFieldVisibility(
        props.checklistField.checklistVisibleFunction,
        activeProductFormValues,
    );
}

function checklistFieldHasVisibilityFunction() {
    return props.checklistField.checklistVisibleFunction != null;
}

function onUpdateStoreFieldValue({
    pId,
    value,
    productFormId,
}: {
    pId: string;
    value: ConfiguratorValue;
    productFormId: number;
}) {
    if (props.activeProductFormId == null) {
        return;
    }
    store.dispatch('configurator/updateActiveProductFormValue', {
        pId,
        value,
        productFormId: props.activeProductFormId,
    });
}

function isDropdownElementVisible() {
    const element = document.querySelector(
        `.ant-select-dropdown--single.c-dropdown-${props.checklistField.pId}-${props.itemRowIndex}`,
    ) as HTMLElement;

    if (element == null) {
        return false;
    }

    return element.style.display !== 'none';
}

async function focusNextRowElement(nextRowElement: HTMLElement) {
    if (!nextRowElement) return;

    const elementToBeFocused = nextRowElement.querySelector('input, select') as
        | HTMLInputElement
        | HTMLSelectElement
        | null;

    if (isDropdown(nextRowElement) || nextRowElement instanceof HTMLInputElement || !elementToBeFocused) {
        nextRowElement.focus();
        if (nextRowElement instanceof HTMLInputElement) {
            await nextTick();
            nextRowElement.select(); // Select text if it's an input
            nextRowElement.setSelectionRange(0, nextRowElement.value.length);
        }
    } else {
        elementToBeFocused.focus();
        if (elementToBeFocused instanceof HTMLInputElement) {
            await nextTick();
            elementToBeFocused.select(); // Select text if it's an input
            elementToBeFocused.setSelectionRange(0, elementToBeFocused.value.length);
        }
    }
}

function isDropdown(element: Element | null): boolean {
    if (element == null) {
        return false;
    }

    return element.classList.contains('c-dropdown');
}

async function onPressEnter(event: Event) {
    if (isDropdown(event.target as Element) && isDropdownElementVisible()) {
        return;
    }

    if (props.isLast) {
        if (props.addNewRow == null) {
            return;
        }
        await props.addNewRow(1);
    }
    const pid: string = props.checklistField.pId;

    const nextRowElement = document.querySelector(
        `.c-checklist__row-value[pid="${pid}"][itemrowindex="${props.itemRowIndex + 1}"]`,
    );
    nextTick(() => {
        focusNextRowElement(nextRowElement as HTMLElement);
    });
}

async function onPressDown(event: Event) {
    if (props.isLast) {
        return;
    }
    const nextRowElement = document.querySelector(
        `.c-checklist__row-value[pid="${props.checklistField.pId}"][itemrowindex="${props.itemRowIndex + 1}"]`,
    );
    nextTick(() => {
        focusNextRowElement(nextRowElement as HTMLElement);
    });
}

async function onPressUp(event: Event) {
    if (props.isFirst) {
        return;
    }
    const nextRowElement = document.querySelector(
        `.c-checklist__row-value[pid="${props.checklistField.pId}"][itemrowindex="${props.itemRowIndex - 1}"]`,
    );
    nextTick(() => {
        focusNextRowElement(nextRowElement as HTMLElement);
    });
}

async function onPressLeft(event: Event) {
    if (props.isLast) {
        return;
    }
    const currentRowElement = document
        .querySelector(
            `.c-checklist__row-value[pid="${props.checklistField.pId}"][itemrowindex="${props.itemRowIndex}"]`,
        )
        ?.closest('.c-checklist__row');

    const prevElement = currentRowElement?.previousElementSibling;

    if (prevElement == null) {
        return;
    }

    nextTick(() => {
        focusNextRowElement(prevElement as HTMLElement);
    });
}

async function onPressRight(event: Event) {
    if (props.isLast) {
        return;
    }
    const currentRowElement = document
        .querySelector(
            `.c-checklist__row-value[pid="${props.checklistField.pId}"][itemrowindex="${props.itemRowIndex}"]`,
        )
        ?.closest('.c-checklist__row');

    const nextElement = currentRowElement?.nextElementSibling;

    if (nextElement == null) {
        return;
    }

    nextTick(() => {
        focusNextRowElement(nextElement as HTMLElement);
    });
}

function setFieldErrorState(data: { rowKey: number; pid: string; errorInField: boolean }) {
    const rowIndexUnavailable = !props.itemRowIndex && props.itemRowIndex !== 0;
    const isCorrectField =
        (rowIndexUnavailable || data.rowKey === props.itemRowIndex) && data.pid === props.checklistField.pId;

    if (!isCorrectField) {
        return;
    }

    if (data.errorInField) {
        hasError.value = true;
    } else {
        hasError.value = false;
    }
}

EventBus.$on(EventBusEvents.triggerVisibilityFunctions, triggerVisibilityFunctions);
EventBus.$on(EventBusEvents.setProductFieldErrorState, setFieldErrorState);

onBeforeUnmount(() => {
    EventBus.$off(EventBusEvents.triggerVisibilityFunctions, triggerVisibilityFunctions);
    EventBus.$off(EventBusEvents.setProductFieldErrorState, setFieldErrorState);
});

return { __sfc: true,props, proxy, imageWrapper, richTextEditor, fieldType, isFieldVisible, isModalVisible, selectedChecklist, showFullScreenImage, hasError, isFieldDisabled, determineIsFieldVisible, isRoltekUG, labelFontClass, labelClasses, className, activeFieldModule, toggleImageFullScreen, checkVisibilityLevel, descriptionExists, toggleInfoDetailsModal, triggerVisibilityFunctions, checklistFieldHasVisibilityFunction, onUpdateStoreFieldValue, isDropdownElementVisible, focusNextRowElement, isDropdown, onPressEnter, onPressDown, onPressUp, onPressLeft, onPressRight, setFieldErrorState, RichTextEditor }
}

})